import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button, Grid } from '@mui/material';
import { getUserSelector } from '@store/auth';
import { getModalSelector } from '@store/modals';
import { ModalsService, PopupsService, WisperUsersService } from '@services';
import { MaskUtil } from '@utils';
import { ErrorsEnum, Modals, Popups, StatusesEnum } from '@enums';
import { Form, FormField, Modal, TextField } from '@elements';
import { getAddNewWisperUserSchema } from './add-new-wisper-user.schema';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

/**
 * Add new wisper user modal component.
 *
 * @author Ihar Kazlouski
 * @function AddNewWisperUserModal
 * @category Modals
 * @return {FC} add new wisper user modal component.
 */
var AddNewWisperUserModal = function AddNewWisperUserModal() {
  var _modalData$phone_numb;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var modal = useSelector(getModalSelector(Modals.AddNewWisperUser));
  var authUser = useSelector(getUserSelector);
  var modalData = modal === null || modal === void 0 ? void 0 : modal.data;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      phoneError = _useState2[0],
      setPhoneError = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      emailError = _useState4[0],
      setEmailError = _useState4[1];

  var formRef = useRef();
  /**
   * Submit form.
   *
   * @author Ihar Kazlouski
   * @param {AddNewOrganizationValues} values values.
   * @return {Promise<void>}
   */

  var handleSuccess = function handleSuccess() {
    PopupsService.open(Popups.Success, {
      header: t('components.popups.success'),
      body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.userUpdated') : t('components.modals.addNewUser.userAdded'),
      acceptButton: t('components.popups.ok'),
      width: 270,
      maxWidth: 'xs'
    }).then(function () {
      ModalsService.close(Modals.AddNewWisperUser);
      modalData === null || modalData === void 0 ? void 0 : modalData.updateAfterEdit();
    })["catch"](function () {
      ModalsService.close(Modals.AddNewWisperUser);
      modalData === null || modalData === void 0 ? void 0 : modalData.updateAfterEdit();
    });
  };
  /**
   * Submit form.
   *
   * @author Ihar Kazlouski
   * @param {AddNewWisperUserValues} values values.
   * @return {Promise<void>}
   */


  var handleSubmit = function handleSubmit(values) {
    return function () {
      void _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var error, _modalData$tenant, _error;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(modalData !== null && modalData !== void 0 && modalData.isEdit)) {
                  _context.next = 13;
                  break;
                }

                _context.prev = 1;
                _context.next = 4;
                return WisperUsersService.apiUpdateWisperUserPut((authUser === null || authUser === void 0 ? void 0 : authUser.id) || '', {
                  body: {
                    first_name: values.firstName,
                    last_name: values.lastName,
                    email: values.email,
                    phone_number: '+' + values.phoneNumber,
                    id: modalData === null || modalData === void 0 ? void 0 : modalData.id,
                    tenant: modalData === null || modalData === void 0 ? void 0 : modalData.tenant
                  }
                });

              case 4:
                handleSuccess();
                _context.next = 11;
                break;

              case 7:
                _context.prev = 7;
                _context.t0 = _context["catch"](1);
                error = _context.t0;

                if (error.errorCode === ErrorsEnum.PhoneIsAlreadyExists) {
                  setPhoneError(t('components.modals.addNewUser.phoneError'));
                } else if (error.errorCode === ErrorsEnum.StartsWithZero) {
                  setPhoneError(t('components.modals.addNewUser.startWithZero'));
                } else if (error.errorCode === ErrorsEnum.EmailIsAlreadyExists) {
                  setEmailError(t('components.modals.addNewUser.emailError'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewUser.updateError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 11:
                _context.next = 23;
                break;

              case 13:
                _context.prev = 13;
                _context.next = 16;
                return WisperUsersService.apiAddWisperUserPost((authUser === null || authUser === void 0 ? void 0 : authUser.id) || '', {
                  body: {
                    users: [{
                      first_name: values.firstName,
                      last_name: values.lastName,
                      email: values.email,
                      phone_number: '+' + values.phoneNumber,
                      tenant: modalData === null || modalData === void 0 ? void 0 : (_modalData$tenant = modalData.tenant) === null || _modalData$tenant === void 0 ? void 0 : _modalData$tenant.id
                    }]
                  }
                });

              case 16:
                handleSuccess();
                _context.next = 23;
                break;

              case 19:
                _context.prev = 19;
                _context.t1 = _context["catch"](13);
                _error = _context.t1;

                if (_error.errorCode === ErrorsEnum.PhoneIsAlreadyExists) {
                  setPhoneError(t('components.modals.addNewUser.phoneError'));
                } else if (_error.errorCode === ErrorsEnum.StartsWithZero) {
                  setPhoneError(t('components.modals.addNewUser.startWithZero'));
                } else if (_error.errorCode === ErrorsEnum.EmailIsAlreadyExists) {
                  setEmailError(t('components.modals.addNewUser.emailError'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewUser.addError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 23:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[1, 7], [13, 19]]);
      }))();
    };
  };
  /**
   * Close modal.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var closeModal = function closeModal() {
    var _formRef$current, _formRef$current$getS;

    if (!(formRef !== null && formRef !== void 0 && (_formRef$current = formRef.current) !== null && _formRef$current !== void 0 && (_formRef$current$getS = _formRef$current.getState()) !== null && _formRef$current$getS !== void 0 && _formRef$current$getS.pristine)) {
      PopupsService.open(Popups.Default, {
        header: t('components.popups.unsavedChanges'),
        body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.quitEditModal') : t('components.modals.addNewUser.quitAddingModal'),
        acceptButton: t('components.popups.yes'),
        rejectButton: t('components.popups.cancel'),
        maxWidth: 'xs',
        width: 270
      }).then(function () {
        return ModalsService.close(Modals.AddNewWisperUser);
      })["catch"](function () {// error handling
      });
    } else {
      ModalsService.close(Modals.AddNewWisperUser);
    }
  };
  /**
   * Handle change function.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleChange = function handleChange() {
    if (phoneError) {
      setPhoneError(null);
    }

    if (emailError) {
      setEmailError(null);
    }
  };
  /**
   * Parse script tags.
   *
   * @author Ihar Kazlouski
   * @param {unknown} value value.
   * @return {void}
   */


  var handleScriptParse = function handleScriptParse(value) {
    var parseValue = value;
    var regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    return parseValue.replace(regex, '');
  };

  return /*#__PURE__*/_jsxs(Modal, {
    fullWidth: true,
    maxWidth: "md",
    children: [/*#__PURE__*/_jsx(Modal.Title, {
      handleClose: closeModal,
      children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.edit') : t('components.modals.addNewUser.title')
    }), /*#__PURE__*/_jsx(Modal.Content, {
      children: /*#__PURE__*/_jsx(Form, {
        onSubmit: handleSubmit,
        subscription: {
          pristine: true,
          submitting: true,
          invalid: true,
          values: true
        },
        initialValues: {
          firstName: (modalData === null || modalData === void 0 ? void 0 : modalData.first_name) || '',
          lastName: (modalData === null || modalData === void 0 ? void 0 : modalData.last_name) || '',
          email: (modalData === null || modalData === void 0 ? void 0 : modalData.email) || '',
          phoneNumber: (modalData === null || modalData === void 0 ? void 0 : (_modalData$phone_numb = modalData.phone_number) === null || _modalData$phone_numb === void 0 ? void 0 : _modalData$phone_numb.substring(1)) || ''
        },
        validateSchema: getAddNewWisperUserSchema(phoneError, emailError),
        render: function render(_ref2) {
          var pristine = _ref2.pristine,
              submitting = _ref2.submitting,
              invalid = _ref2.invalid,
              values = _ref2.values,
              form = _ref2.form;
          formRef.current = form;
          return /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Grid, {
              container: true,
              spacing: 2,
              children: [/*#__PURE__*/_jsxs(Grid, {
                container: true,
                item: true,
                spacing: 5,
                children: [/*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "firstName",
                    size: "small",
                    component: TextField,
                    parse: handleScriptParse,
                    inputProps: {
                      maxLength: 100
                    },
                    fullWidth: true,
                    label: t('components.modals.addNewUser.firstName')
                  })
                }), /*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "lastName",
                    size: "small",
                    component: TextField,
                    parse: handleScriptParse,
                    inputProps: {
                      maxLength: 100
                    },
                    fullWidth: true,
                    label: t('components.modals.addNewUser.lastName')
                  })
                })]
              }), /*#__PURE__*/_jsxs(Grid, {
                container: true,
                item: true,
                spacing: 5,
                children: [/*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "phoneNumber",
                    size: "small",
                    component: TextField,
                    fullWidth: true,
                    placeholder: "+1 912-123-1234",
                    label: t('components.modals.addNewUser.phoneNumber'),
                    maskProps: MaskUtil.phoneMask,
                    onChange: handleChange,
                    disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && (modalData === null || modalData === void 0 ? void 0 : modalData.status) !== StatusesEnum.New
                  })
                }), /*#__PURE__*/_jsx(Grid, {
                  item: true,
                  xs: 6,
                  children: /*#__PURE__*/_jsx(FormField, {
                    name: "email",
                    size: "small",
                    component: TextField,
                    onChange: handleChange,
                    inputProps: {
                      maxLength: 100
                    },
                    fullWidth: true,
                    label: t('components.modals.addNewUser.email')
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Box, {
              pt: 6,
              width: "100%",
              children: /*#__PURE__*/_jsx(Button, {
                variant: "contained",
                disabled: pristine || submitting || invalid,
                fullWidth: true,
                onClick: handleSubmit(values),
                children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.save') : t('components.modals.addNewUser.add')
              })
            })]
          });
        }
      })
    })]
  });
};

export { AddNewWisperUserModal };